export const escapeXml = (unsafe) => {
  if (typeof unsafe !== 'string') return unsafe;
  return unsafe.replace(/[<>&'"]/g, (c) => {
    switch (c) {
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '&': return '&amp;';
      case '\'': return '&apos;';
      case '"': return '&quot;';
      default: return c;
    }
  });
};

export const formatXml = (xml, indent = '  ') => {
  if (!xml || typeof xml !== 'string') return xml;
  let depth = 0;
  const lines = [];
  // Split on tag boundaries, keeping delimiters
  const tokens = xml.replace(/>\s*</g, '><').split(/(?<=>)(?=<)/);
  for (const token of tokens) {
    const trimmed = token.trim();
    if (!trimmed) continue;
    const isClosing = /^<\//.test(trimmed);
    const isSelfClosing = /\/>$/.test(trimmed);
    const isComment = /^<!--/.test(trimmed);
    const isOpening = !isClosing && !isSelfClosing && /^<[^!?]/.test(trimmed);
    // Check if this is an inline element: <TAG>text</TAG> all in one token
    const isInline = /^<[^/][^>]*>[^<]+<\/[^>]+>$/.test(trimmed);
    if (isClosing) depth = Math.max(0, depth - 1);
    lines.push(indent.repeat(depth) + trimmed);
    if (isOpening && !isInline && !isComment) depth++;
  }
  return lines.join('\n');
};

export const buildFilterPreview = (filter) => {
  if (!filter || filter.trim() === '') return '';
  
  const patterns = [
    { regex: /^([\w\.]+)\s*=\s*'([^']*)'$/i, xml: (m) => `<equalto><field>${m[1]}</field><value>${m[2]}</value></equalto>` },
    { regex: /^([\w\.]+)\s*=\s*(\d+)$/i, xml: (m) => `<equalto><field>${m[1]}</field><value>${m[2]}</value></equalto>` },
    { regex: /^([\w\.]+)\s*!=\s*'([^']*)'$/i, xml: (m) => `<notequalto><field>${m[1]}</field><value>${m[2]}</value></notequalto>` },
    { regex: /^([\w\.]+)\s*>\s*(\d+)$/i, xml: (m) => `<greaterthan><field>${m[1]}</field><value>${m[2]}</value></greaterthan>` },
    { regex: /^([\w\.]+)\s*>=\s*(\d+)$/i, xml: (m) => `<greaterthanorequalto><field>${m[1]}</field><value>${m[2]}</value></greaterthanorequalto>` },
    { regex: /^([\w\.]+)\s*<\s*(\d+)$/i, xml: (m) => `<lessthan><field>${m[1]}</field><value>${m[2]}</value></lessthan>` },
    { regex: /^([\w\.]+)\s*<=\s*(\d+)$/i, xml: (m) => `<lessthanorequalto><field>${m[1]}</field><value>${m[2]}</value></lessthanorequalto>` },
    { regex: /^([\w\.]+)\s+LIKE\s+'([^']*)'$/i, xml: (m) => `<like><field>${m[1]}</field><value>${m[2]}</value></like>` },
  ];
  
  const hasAnd = / AND /i.test(filter);
  const hasOr = / OR /i.test(filter);
  
  if (!hasAnd && !hasOr) {
    for (const p of patterns) {
      const match = filter.match(p.regex);
      if (match) return `<filter>\n          ${p.xml(match)}\n        </filter>`;
    }
    return `<filter><!-- ${escapeXml(filter)} --></filter>`;
  }
  
  const conditions = filter.split(/ AND | OR /i);
  let innerXml = conditions.map(cond => {
    const trimmed = cond.trim();
    for (const p of patterns) {
      const match = trimmed.match(p.regex);
      if (match) return p.xml(match);
    }
    return `<!-- ${escapeXml(trimmed)} -->`;
  }).join('\n            ');
  
  const wrapper = hasOr && !hasAnd ? 'or' : 'and';
  return `<filter>
          <${wrapper}>
            ${innerXml}
          </${wrapper}>
        </filter>`;
};

export const parseFilterString = (filterStr) => {
  if (!filterStr) return [{ field: '', operator: '=', value: '', value2: '', logic: 'AND' }];
  
  const parts = filterStr.split(/\s+(AND|OR)\s+/);
  const rows = [];
  
  for (let i = 0; i < parts.length; i += 2) {
    const expression = parts[i];
    const nextLogic = parts[i + 1] || 'AND';
    const match = expression.match(/^([\w\.]+)\s+(=|!=|<=|>=|<|>|LIKE|IN|NOT IN)\s+(.+)$/);
    
    if (match) {
      let op = match[2], val = match[3], val2 = '';
      if ((val.startsWith("'") && val.endsWith("'")) || (val.startsWith('"') && val.endsWith('"'))) {
        val = val.slice(1, -1);
      }
      if ((op === 'IN' || op === 'NOT IN') && val.startsWith('(') && val.endsWith(')')) {
        val = val.slice(1, -1).split(',').map(s => s.trim().replace(/'/g, "")).join(',');
      }
      if (op === 'LIKE') {
        if (val.endsWith('%') && !val.startsWith('%')) {
          op = 'LIKE_START';
          val = val.slice(0, -1);
        } else if (val.startsWith('%') && val.endsWith('%')) {
          op = 'LIKE_CONTAINS';
          val = val.slice(1, -1);
        }
      }
      rows.push({ field: match[1], operator: op, value: val, value2: val2, logic: nextLogic });
    }
  }
  
  return rows.length > 0 ? rows : [{ field: '', operator: '=', value: '', value2: '', logic: 'AND' }];
};